<template>
  <v-data-table
    ref="table"
    v-model="selected"
    v-bind="customSort ? {customSort: customSort }: {}"
    calculate-widths
    class="offers offers-table"
    disable-pagination
    :expanded.sync="expanded"
    fixed-header
    :headers="offersHeaders"
    hide-default-footer
    item-key="index"
    :items.sync="items"
    :multi-sort="false"
    :no-data-text="$t('Tenders.NoData')"
    :no-results-text="$t('Tenders.NoData')"
    :show-select="!without_select"
    :sort-by.sync="sort_by"
    :sort-desc.sync="sort_desc"
    @click:row="expand"
    @toggle-select-all="toggleSelectAllFunc">
    <template
      v-for="(header, i) in offersHeaders"
      #[`header.${header.value}`]="{ props, on }">
      <sCheckbox
        v-if="header.value==='data-table-select'"
        :key="i"
        class="mx-auto"
        :indeterminate="props.indeterminate"
        :value="props.value"
        @input="on.input"/>
      <template v-else>
        {{ header.text }}
        <ResizeColumnDiv
          :key="i"
          ref="resize_column"
          resize_all_tables
          :value="header.value"
          @mouseup="(val, width) => $emit('resizeMouseup', val, width)"/>
      </template>
    </template>
    <template #[`item.name`]="{ item }">
      <BidderInfoCell
        :id="item.id"
        :id-bid-row="item.idBidRow"
        :item="item"
        :lot-id="item.lotId"
        :only-review="only_review"
        :tender="tender"
        @showRequirements="showModalInfo"/>
    </template>
    <template #[`item.lotTitle`]="{ item }">
      <div
        :id="item.id"
        class="lotTitle flex flex-wrap"
        data-qa="offer-title"
        :id-bid-row="item.idBidRow"
        :lot-id="item.lotId">
        <BidderIsWinner
          v-if="item.selectedBid && !offersHeaders.some(el => el.value == 'name')"
          class="mr-2 mt-1"/>
        <div
          :class="{'secondary__green_not_active--text': item.bidStatus == 'DRAFT'}"
          data-qa="offer-name">
          {{ item.lotTitle || findLot(item.lotId) }}
        </div>
      </div>
      <div
        v-if="item.analogTitle"
        class="analogTitle grey--text h8-text"
        data-qa="analog-1">
        <sIcon
          color="grey"
          size="12"
          type="import"/>
        {{ item.analogTitle }}
      </div>
      <ExternalParticipantChip
        v-if="item.foreignBidderBid && !offersHeaders.some(el => el.value == 'name') && !hide_foreign"
        :class="[{ 'ml-5' : item.selectedBid }, 'mt-1']"/>
    </template>
    <template #[`item.lotTitleOrig`]="{ item }">
      <div
        :id="item.id"
        :class="{'secondary__green_not_active--text': item.bidStatus == 'DRAFT'}"
        data-qa="offer-name"
        :id-bid-row="item.idBidRow"
        :lot-id="item.lotId">
        <BidderIsWinner
          v-if="item.selectedBid && isNotProduction"
          class="mr-1"/>
        {{ item.lotTitleOrig || findLot(item.lotId) }}
      </div>
      <div
        v-if="item.analogTitle"
        class="analogTitle grey--text h8-text"
        data-qa="analog-2">
        <sIcon
          color="grey"
          size="12"
          type="import"/>
        {{ item.analogTitle }}
      </div>
      <ExternalParticipantChip
        v-if="item.foreignBidderBid && !offersHeaders.some(el => el.value == 'name') && !hide_foreign"
        :class="[{ 'ml-5' : item.selectedBid }, 'mt-1']"/>
    </template>
    <template #[`item.summ`]="{ item }">
      <div
        :class="{'secondary__green_not_active--text': item.bidStatus == 'DRAFT'}"
        data-qa="currency-price">
        {{ $n(item.currencySumm, 'decimal') }}
        {{ item.currencyTitle }}
      </div>
      <CurrencyConversionTooltip
        v-if="item.price"
        :currency="item.currencyTitle"
        :current-id="item.currencyId"
        :price="item.currencySumm"
        :transform="true"/>
    </template>
    <template #[`item.quantity`]="{ item }">
      <EditSelectedQuantity
        :change_winner_mode.sync="change_winner_mode"
        data-qa="quantity"
        :editable="!only_review && can_edit"
        :item="item"
        show_selected_quantity
        :tender="tender"/>
    </template>
    <template #[`item.timeSupplyBids`]="{ item }">
      <span :class="{'secondary__green_not_active--text': item.bidStatus == 'DRAFT'}">
        {{ formatFullDate(item.timeSupplyBids) }}
      </span>
    </template>
    <template #[`item.timeCancelBid`]="{ item }">
      <span :class="{'secondary__green_not_active--text': item.bidStatus == 'DRAFT'}">
        {{ formatFullDate(item.timeCancelBid) }}
      </span>
    </template>
    <template #[`item.price`]="{ item, index }">
      <template v-if="tender.biddingType.tenderingForPercentage">
        <sTooltip
          :content="$t('Tenders.BidEconomyTooltipText')"
          :disabled="!maxEconomy || item.price !== maxEconomy"
          placement="bottom">
          <div
            :class="{'secondary__green_not_active--text': item.bidStatus == 'DRAFT', 'max-economy': maxEconomy && item.price === maxEconomy}"
            data-qa="bid-economy">
            <span v-if="item.price">{{ $n(item.price, 'decimal') }} %</span>
            <span v-else>-</span>
          </div>
        </sTooltip>
      </template>
      <template v-else>
        <BidPrice
          :item="item"
          :mode="mode"
          :prev-date="index !== 0 ? getPrevTimeBid(item) : null"
          :show_best_price="!show_reducedPrice && item.bidStatus !== 'DRAFT'"
          :tender-id="tender.tenderId"/>
      </template>
    </template>
    <template #[`item.reducedPrice`]="{ item }">
      <v-tooltip
        content-class="price-tooltip tooltip white black--text pa-0"
        top>
        <template #activator="{ on }">
          <div
            :class="{'secondary__green_not_active--text': item.bidStatus == 'DRAFT', ' best-price-chip secondary__green_approved px-2 rounded-lg': item.bestPrice && item.bidStatus !== 'DRAFT'}"
            data-qa="reduced-price"
            v-on="item.bestPrice ? on : ''">
            {{ $formatBidPrice(item.reducedPrice) }}
            {{ item.currencyTitle }}
          </div>
        </template>
        <div class="tooltip-item text-start pa-2">
          <span
            class="black--text h8-text text-start"
            data-qa="reduced-price-tooltip">{{ $t('Tenders.BidPriceTooltipText') }}</span>
        </div>
      </v-tooltip>
    </template>
    <template #[`item.status`]="{ item }">
      <StatusChip
        v-if="item.prequalification && item.prequalification.status"
        key-name="status"
        :name="$t('Tenders.prequalification.' + item.prequalification.status)"
        :status="item.prequalification"/>
    </template>
    <template #[`item.statusReduction`]="{ item }">
      <StatusChip
        v-if="item.bidDecisionAttribute !== 'DEFAULT'"
        :color="item.bidDecisionAttribute === 'DISQUALIFIED' ? 'var(--color-error)' : (item.bidDecisionAttribute === 'WINNER' ? 'var(--color-warning)' : 'var(--color-warning)')"
        key-name="status-reduction"
        :name="$t('Tenders.stateBidReduction.' + item.bidDecisionAttribute)"
        :status="item.prequalification"
        text-color="var(--text-color)"/>
    </template>
    <template #[`item.copy_attachments`]="{ item }">
      <sTooltip
        :border="false"
        :content="copyAttachmentTooltip(item)"
        :open-delay="500"
        placement="bottom"
        popper-class="bid-no-attachment info-tooltip"
        type="white">
        <sCheckbox
          v-model="item.copy_attachments"
          data-qa="copy_attachments"
          :disabled="!item.attachments || !item.attachments.length"
          @change="val => selectAttachments(val, item)"/>
      </sTooltip>
    </template>
    <template #[`item.isCanceled`]="{ item }">
      <CancelledBidInfo :bid="item"/>
    </template>
    <template #[`item.attachments`]="{ item }">
      <sTooltip
        class="item"
        placement="top-end">
        <div slot="content">
          <div>{{ `${$t('Tenders.bidAttachments')} - ${item.attachmentCount}` }}</div>
          <b v-if="!disabledDocumentArchive && item.attachmentCount">{{ $t('Tenders.DownloadArchiveDocuments') }}</b>
          <div
            v-else-if="item.attachmentCount"
            style="color: var(--color-error)">
            {{ $t('Tenders.ArchiveNotAvailableAtStatus') }}
          </div>
        </div>
        <DownloadDocumentArchive
          data-qa="offer-download-document-archive"
          :disabled="disabledDocumentArchive || !item.attachmentCount"
          mode="icon"
          :supplier-id="item.bidderInfo ? item.bidderInfo.organizationId : 0"
          :tender-id="tender.tenderId"
          :title="`${$t('Common.bidderDocumentations', { bidderTitle: item.bidderInfo ? item.bidderInfo.organizationName || '–' : '–', tenderNumber: tender.tenderNumber })}`">
          <span class="my-auto ml-1">{{ item.attachmentCount }}</span>
        </DownloadDocumentArchive>
      </sTooltip>
      <sTooltip
        v-if="contranctProps && item.selectedBid && tender.tenderStatus.id === statuseKeys.COMPLETED"
        class="item mt-1"
        placement="bottom-end">
        <div slot="content">
          <div>{{ $t('Tenders.Contracts') }} - {{ item.contractCount.contract || 0 }}</div>
          <div>{{ $t('Tenders.AdditionalAgreements') }} - {{ item.contractCount.approving || 0 }}</div>
        </div>
        <div>
          <template v-if="!contranctProps || !contranctProps.contractLoaded">
            <v-skeleton-loader
              :boilerplate="false"
              :tile="false"
              type="text"/>
          </template>
          <IconCount
            v-else
            :count="item.contractCount.all || 0"
            icon="signy"
            :icon-color="!item.contractCount.contract ? 'grey': (item.contractStatus && item.contractStatus.color ? item.contractStatus && item.contractStatus.color : '')"
            :text-class="{ 'grey--text': !item.contractCount.contract }"/>
        </div>
      </sTooltip>
    </template>
    <template #expanded-item="{ item }">
      <slot
        :item="item"
        name="expanded-item">
        <td
          class="sub-data"
          :colspan="1"/>
        <td
          class="sub-data"
          :colspan="offersHeaders.length - 2">
          <OfferExpandedDetails
            v-if="expanded.some(el => el.index == item.index)"
            :item="item"
            show-contracts
            :tender="tender"/>
          <div
            v-if="item.additional_fields && item.additional_fields.additionalFields && item.additional_fields.additionalFields.length"
            data-qa="bid-additional-fields">
            <div class="h5-headline text-start">
              {{ $t('AdditionalFields.AdditionalFields') }}
            </div>
            <div
              v-for="(additionalFieldItem, additionalFieldindex) in item.additional_fields.additionalFields"
              :key="additionalFieldindex"
              class="py-3"
              :data-qa="`additional-field-${additionalFieldItem.id}`">
              <div class="h9-label px-4 text-start">
                {{ additionalFieldItem.caption }}
              </div>
              <div
                class="black--text h7-text px-4 text-start">
                <template
                  v-if="additionalFieldItem.type === 'URL'">
                  <a
                    :key="additionalFieldItem.id + '-' + item.id"
                    :href="additionalFieldItem.value"
                    rel="nofollow"
                    target="_blank">{{ additionalFieldItem.value }}</a>
                </template>
                <template v-else>
                  {{ additionalFieldItem.value || '–' }}
                </template>
              </div>
            </div>
          </div>
          <AdditionalFieldsDtek
            v-if="item.additional_fields && item.additional_fields.dtek"
            :value="item.additional_fields.dtek"/>
        </td>
        <td
          v-if="!tender.biddingType.tenderingForPercentage"
          class="sub-data"
          :colspan="1"/>
      </slot>
    </template>
    <template #[`body.append`]>
      <OfferRequirementsInfo :requirement.sync="requirementsModalInfo"/>
    </template>
  </v-data-table>
</template>
<script>
import IconCount from 'components/common/elements/IconCount.vue'
import BidderIsWinner from 'components/tenders/BidderIsWinner.vue'
import StatusChip from 'components/common/elements/StatusChip.vue'
import CancelledBidInfo from 'components/tenders/CancelledBidInfo.vue'
import BidPrice from 'components/tenders/tender page/tender tabs/BidPrice.vue'
import ResizeColumnDiv from 'components/common/blocks/table/ResizeColumnDiv.vue'
import AdditionalFieldsDtek from 'components/tenders/tender page/AdditionalFieldsDtek.vue'
import DownloadDocumentArchive from 'components/common/blocks/DownloadDocumentArchive.vue'
import BidderInfoCell from 'components/tenders/tender page/offersGroups/BidderInfoCell.vue'
import OfferRequirementsInfo from 'components/tenders/tender page/tender tabs/OfferRequirementsInfo.vue'
import ExternalParticipantChip from 'components/tenders/tender page/ExternalParticipantChip.vue'
import CurrencyConversionTooltip from 'components/common/elements/CurrencyConversionTooltip.vue'
import EditSelectedQuantity from 'components/tenders/tender page/tender tabs/EditSelectedQuantity.vue'
import OfferExpandedDetails from 'components/tenders/tender page/offersGroups/OfferExpandedDetails.vue'

/* eslint-disable vue/prop-name-casing */
import { snackbarMixin, modalMixin, tenderMixin } from 'mixins'
import tour from 'mixins/tour'
import tableMixin from 'mixins/tableMixin'
import tableSelectionMixin from 'mixins/tableSelectionMixin'
import offersHeadersMixin from 'mixins/offersHeadersMixin'
import moment from 'moment'

import { eventBus } from '@/main'

export default {
  name: 'offersTable',
  mixins: [
    snackbarMixin,
    modalMixin,
    tenderMixin,
    tour,
    tableMixin,
    tableSelectionMixin,
    offersHeadersMixin,
  ],
  data() {
    return {
      expanded: [],
      selected: [],
      sort_by: [],
      sort_desc: [],
      maxEconomy: null,
      offersHeaders: [],
      requirementsModalInfo: null
    }
  },
  methods: {
    showModalInfo(requirement) {
      this.requirementsModalInfo = requirement
    },
    createHeader() {
      if (this.headers?.length) {
        this.offersHeaders = this.headers
        return
      }
      let status = this.tender.tenderStatus?.id
      let headers = [
        { text: '', value: 'data-table-select', width: '20px' },
        { text: this.$t('Tenders.Supplier'), value: 'name', coef_width: '2.5' },
        { text: this.$t('Tenders.Lot'), value: 'lotTitle', coef_width: '3'},
        { text: this.$t('Catalog.Amount'), value: 'quantity', coef_width: '2', class: 'header_break-spaces' },
        { text: this.$t('Catalog.Price'), value: 'price', coef_width: '1.2' },
        { text: this.$t('Tenders.TotalCostShort'), value: 'summ', coef_width: '1.2' }
      ]

      if (this.$can('is', 'tenderProposalShowIp') && this.mode !== 'history') {
        headers.push({ text: 'IP', value: 'ipAddress', width: '130px' })
      }
      headers.push({ text: '', value: 'attachments', width: '40px', sortable: false })

      this.$_handleTenderingForPercentage(headers)
      this.$_handleReducedPrice(headers)
      this._handleReductionStatus(headers, status)
      this._handlePrequalificationStatus(headers, status)
      this.$_handlePriceVisibility(headers)
      if ( this.formatHeaders ) {
        headers = this.formatHeaders(headers) //  width в індексі - без пікселів чи відсотків  вичислається в  this.$_getHeadersWidthByCoef(headers)
      }
      this._handleSelectColumn(headers)
      
      this.$_getHeadersWidthByCoef(headers)
      this.offersHeaders = headers
    },
    _handleReductionStatus(headers, status) {
      // редакцион + анализ или терминальные стадии
      const isReduction = this.tender?.biddingType?.multipleRoundsOnAuction && [this.statuseKeys.BID_ANALYSIS, this.statuseKeys.COMPLETED, this.statuseKeys.PURCHASE_NOT_PLACED, this.statuseKeys.CANCELED].includes(status)
      if (isReduction) {
        headers.splice(-1, 0,
          { text: this.$t('Purchasing.Status'), value: 'statusReduction', sortable: false, coef_width: '1.5' })
      }
    },
    _handlePrequalificationStatus(headers, status) {
      if (status == this.statuseKeys.PREQUALIFICATION || (status == this.statuseKeys.COMPLETED && this.tender?.biddingType?.isFrameFirstStage)) {
        headers.splice(-1, 0,
          { text: this.$t('Purchasing.Status'), value: 'status', sortable: false, coef_width: '1.5' })
      }
    },
    _handleSelectColumn(headers) {
      if (this.without_select) {
        headers[0].value = ''
      }
    },
    getPrevTimeBid(item) {
      const allItems = this.items.filter(i => i.lotId === item.lotId
       && i.participant === item.participant
        && new Date(i.timeSupplyBids).getTime() < new Date(item.timeSupplyBids
        ).getTime())
      if (!allItems?.length) {
        return null
      }
      return allItems
        .sort((a, b) => new Date(b.timeSupplyBids).getTime() - new Date(a.timeSupplyBids).getTime())[0].timeSupplyBids
    },
    toggleSelectAllFunc(arg) {
      this.selected = this.toggleSelectAll(arg, this.selected) || this.selected
    },
    onSelectionChange(val) {
      if (this.group) {
        this.selecting(val, this.group)
        return
      }
      if (val.length > 0) {
        this.$_openSnackbar(typeof this.snackbar_open == 'object' ?
          this.snackbar_open : this.snackbar_open(val))
      } else if (val.length == 0) {
        this.$_closeSnackbar()
      }
    },
    formatDate(value) {
      if (value) {
        return moment(value).format('DD.MM.YYYY, HH:mm')
      }
    },
    formatFullDate(value) {
      if (value) {
        return moment(value).format('DD MMMM, HH:mm')
      } else {
        return value || '–'
      }
    },
    getMaxEconomy() {
      this.maxEconomy = Math.max.apply(Math, this.items?.map((o) => { return o.price }))
    },
    expand(val) {
      if (this.without_expanded) { return }
      if (this.expanded.find(el => el.index == val.index)) {
        this.expanded = this.expanded.filter(el => el.index != val.index)
      } else {
        this.expanded.push(val)
      }

      this.$_getPropFromDBStorage('offer-archive_tour', false)
        .then(async (viewedTour) => {
          if (viewedTour) { return }
          let tour = [{
            dataKey: 'offer-download-document-archive',
            title: this.$t('Common.PleaseNote'),
            text: this.$t('Common.downloadArhiveHint'),
            step: 1,
          }]
          eventBus.$emit('startTour', tour)
          await this.$_savePropToDBStorage('offer-archive_tour', true)
        })
    },
    updItems() {
      let quantity = el => ((this.show_selected && this.show_selected_quantity ? el.selectedQuantity : el.quantity) || el.quantity)

      this.$_.forEach(this.items, el => {
        el.summ = quantity(el) * el.price,
        el.currencySumm = this.tender.biddingType.recalcTotalSummByReducedPrice
          ? quantity(el) * el.reducedPrice // https://app.clickup.com/t/8678paa3u Моя приведена ціна*Кількість
          : quantity(el) * el.currencyPrice // Ціна грн. за 1*Кількість
        el.attachmentCount = el?.attachments?.length// +
        el.all_criteria?.forEach((criteriaItem) => {
          if (!Array.isArray(criteriaItem?.items)) { return }
          el.attachmentCount += criteriaItem.items.reduce((sum, i) => sum + (i.attachments?.length ?? 0), 0)
        })
      })
    },
    selectAttachments(val, item) {
      this.items.forEach(bid => {
        if (bid.participant == item.participant) {
          bid.copy_attachments = val
        }
      })
    },
    copyAttachmentTooltip(item) {
      return item.attachments?.length ? this.$t('Tenders.CopyAttachmentsToAllProposal') : this.$t('Tenders.NoAttachmentsToProposal')
    },
  },
  computed: {
    /**
     * На этих статусах нельзя загружать архивом
      1: {statusCode: 1, title: "Реєстрація"}
      4: {statusCode: 4, title: "Прийом пропозицій"}
      7: {statusCode: 7, title: "Аукціон"}
     */
    disabledDocumentArchive() {
      return [this.statuseKeys.REGISTRY, this.statuseKeys.SUBMISSION, this.statuseKeys.AUCTION].includes(this.tender?.tenderStatus?.id)
    },
    /**
      7: {statusCode: 10, title: "Завершено"}
      8: {statusCode: 11, title: "Закупівля не відбулась"}
      9: {statusCode: 12, title: "Закупівля відмінена"}
     */
    show_selected() {
      return [this.statuseKeys.BID_ANALYSIS, this.statuseKeys.COMPLETED, this.statuseKeys.PURCHASE_NOT_PLACED, this.statuseKeys.CANCELED].includes(this.tender.tenderStatus.id)
    },
    can_edit() {
      if (this.$route.name === 'bidsHistoryByParticipants') {
        return false
      }
      return this.$_checkCanEdit(this.tender, this.$_agreements)
    }
  },
  watch: {
    change_winner_mode(val) {
      if (!val && this.selected?.length) {
        this.selected = []
      }
    },
    tender: {
      immediate: true,
      deep: true,
      handler(val, old) {
        if (val?.tenderId !== old?.tenderId) {
          this.createHeader()
        }
      }
    },
    tab(val) {
      if (val.value != 'offers' && this.selected?.length) {
        this.selected = []
      }
    },
    items(val, old) {
      this.updItems()
      if (this.$refs.table && (val?.length < old?.length || (this.group && !this.group.selected?.length))) {
        this.$refs.table.lastEntry = -1 // якщо до зміни кількості позиицій були вибрані в компоненті таблиці зберігається останній вибраний який часом може не існувати через це помилка
        this.selected = []
      }
      this.$_createResizeColumn(this.$refs.table)
    },
  },
  props: {
    /*
      тільки перегляд - без редагування і переходу
    */
    only_review: {
      type: Boolean,
      default: false
    },
    snackbar_open: [Object, Function],
    items: Array,
    criteryCard: Object,
    headers: Array,
    formatHeaders: Function,
    tender: Object,
    show_selected_quantity: Boolean,
    findLot: {
      type: Function,
      default: () => {}
    },
    without_expanded: Boolean,
    without_select: Boolean,
    currencies: {
      type: Array,
      default: () => []
    },
    change_winner_mode: Boolean,
    tab: [Number, Object],
    /*
      функція власного сортування якшо ц потрібно
    */
    customSort: {
      type: Function,
      // default: items => items
    },
    /*
      Emits when the select-all checkbox in table header is clicked. This checkbox is enabled by the show-select prop
      https://vuetifyjs.com/en/api/v-data-table/#events-toggle-select-all
    */
    toggleSelectAll: {
      type: Function,
      default: () => {}
    },
    /**
     * приховати чіпс "зовнішній"
     */
    hide_foreign: {
      type: Boolean
    },
    /**
     * Место где используется таблица.
     * На текущий момент, нужно только для правильной обработки результата запроса по изменению цены
     */
    mode: {
      type: String
    },
    contranctProps: {
      type: Object
    }
  },
  mounted() {
    this.$root.$on('offerTab_getRequirements', this.clearSelection)
    if (this.items?.length && this.tender.biddingType.tenderingForPercentage) {
      this.getMaxEconomy()
    }
    this.updItems()

    this.$_createResizeColumn(this.$refs.table)
    //this.$_setContracts(this.tender?.bids)
  },
  beforeDestroy() {
    this.$root.$off('offerTab_getRequirements', this.clearSelection)
  },
  components: {
    BidPrice,
    IconCount,
    StatusChip,
    BidderInfoCell,
    BidderIsWinner,
    ResizeColumnDiv,
    CancelledBidInfo,
    EditSelectedQuantity,
    AdditionalFieldsDtek,
    OfferRequirementsInfo,
    OfferExpandedDetails,
    ExternalParticipantChip,
    DownloadDocumentArchive,
    CurrencyConversionTooltip
  }
}
</script>
<style lang="scss">
.offers-table.offers {
  background-color: transparent !important;
   tr td.sub-data {
    padding: 5px !important;
  }
  .resize-column {
    bottom: 0;
  }
  & {
    tr,
    tr.v-data-table__expanded__row {
      th,
      td:not(.sub-data),
      td:last-child:not(.sub-data) {
        border-left: thin solid rgba(var(--black-color-rgba),.12) !important;
      }
    }
    tr.v-data-table__expanded.v-data-table__expanded__row {
      td:nth-child(1),
      td:last-child {
        border-bottom: thin solid rgba(var(--black-color-rgba),.12) !important;
      }
    }
  }
}
</style>

<style lang="scss" scoped>
  .max-economy {
    background: var(--color-success);
    border-radius: 25px;
    padding: 1px 9px;
    display: inline;
  }
  .price-tooltip {
    height: 63px;
    width: 228px;
    background: var(--white-color);
    line-height: 16px;
  }
</style>
<style lang="scss">
.offers-table {
  color: var(--text-color) !important;

  &>.v-data-table__wrapper {
    overflow-y: hidden !important;
  }
  &.v-data-table tr {
    cursor: pointer;
    background-color: var(--v-main__light_green-base) !important;
    .header_break-spaces {
       span {
        white-space: break-spaces !important;
      }
    }
    &.v-data-table__expanded {
      background-color: var(--v-secondary__green_chosen-base) !important;
      .additional-description {
        padding: 16px 0 24px !important;
      }

      &.v-data-table__expanded__content td {
        cursor: default;
        vertical-align: top;
      }
    }

    &:hover:not(.v-data-table__expanded) {
      background-color: var(--v-main__light_green-base) !important;
    }

    .best-price-chip {
      height: unset;
      width: fit-content;
      color: black !important;
      white-space: normal !important;
    }
  }
}
</style>
